<!--
  FairRail.vue — thin Vue 3 wrapper around the framework-agnostic runtime
  (`public/fair-rail/fair-rail.js`, black box — see design/fair-rail-sources/INTEGRATION.md).
  The wrapper owns ONLY the container element and the mount/destroy lifecycle.
  It must not render anything inside the container: the runtime builds its own DOM there.

  The runtime and its stylesheet are static files served by URL (not bundled):
  the script is loaded with a bare dynamic import, the stylesheet with a <link>.

  <FairRail class="hero-rail" assets="/fair-rail/" :options="{ fit:'design', rackWidth:0.72, tubeAt:0.35 }" />
-->
<script setup lang="ts">
import { onBeforeUnmount, onMounted, ref, watch } from 'vue'

export interface FairRailInstance {
  nudge: (name: string | null, impulse?: number) => void
  setPhysics: (physics: Record<string, number>) => void
  destroy: () => void
}

interface FairRailOptions {
  fit?: 'design' | 'cover' | 'contain'
  rackWidth?: number
  tubeAt?: number
  bleed?: number
  anchorX?: number
  anchorY?: number
  background?: string
  rackImage?: string
  interactive?: boolean
  maxDPR?: number
  physics?: Record<string, number> | null
}

interface FairRailModule {
  FairRail: {
    mount: (
      container: HTMLElement,
      opts: FairRailOptions & { assets: string; onReady?: (rail: FairRailInstance) => void },
    ) => FairRailInstance
  }
}

const props = withDefaults(
  defineProps<{
    assets?: string
    options?: FairRailOptions
  }>(),
  {
    assets: '/fair-rail/',
    options: () => ({}),
  },
)

const emit = defineEmits<{ ready: [rail: FairRailInstance] }>()

const host = ref<HTMLElement | null>(null)
let rail: FairRailInstance | null = null
let cancelled = false

function ensureStylesheet(base: string) {
  const href = `${base}fair-rail.css`
  if (document.querySelector(`link[data-fair-rail][href="${href}"]`)) return
  const link = document.createElement('link')
  link.rel = 'stylesheet'
  link.href = href
  link.dataset.fairRail = ''
  document.head.appendChild(link)
}

onMounted(async () => {
  const base = props.assets.replace(/\/?$/, '/')
  ensureStylesheet(base)
  // Runtime lives in public/ and is fetched by URL — never through the bundler.
  const mod = (await import(/* @vite-ignore */ `${base}fair-rail.js`)) as FairRailModule
  if (cancelled || !host.value) return
  rail = mod.FairRail.mount(host.value, {
    assets: base,
    ...props.options,
    onReady: (r) => emit('ready', r),
  })
})

onBeforeUnmount(() => {
  cancelled = true
  // The runtime builds `root` asynchronously (manifest → images). Leaving the
  // page before that lands made `destroy()` throw on `this.root.remove()`, and
  // an exception inside unmount left Vue's patch half-done: the next route
  // rendered nothing, so "See all" looked like it never navigated.
  try {
    rail?.destroy()
  } catch {
    // runtime not fully mounted — nothing to tear down
  }
  rail = null
})

// live physics tweaks without remount (e.g. from a design panel)
watch(
  () => props.options.physics,
  (p) => {
    if (rail && p) rail.setPhysics(p)
  },
  { deep: true },
)

defineExpose({
  nudge: (name: string | null, impulse?: number) => rail?.nudge(name, impulse),
  get rail() {
    return rail
  },
})
</script>

<template>
  <!-- the ONLY element this component renders; give it a height via the class you pass in -->
  <div
    ref="host"
    class="fair-rail-host"
  />
</template>

<style scoped>
.fair-rail-host {
  position: relative;
  width: 100%;
  height: 100%;
}
</style>
